import { DocumentTextIcon } from '@heroicons/react/24/outline';
import React, { memo } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { deliverablesApi } from '@/services/api/deliverablesService';
import type { Deliverable } from '@/services/types/deliverables-types';

import { EntityTooltip } from '../core/EntityTooltip';
import { EntityLinkTriggerProps, FetchState } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

// ============================================================================
// Types
// ============================================================================

export interface DeliverableLinkProps extends EntityLinkTriggerProps {
  id: string;
  matterId: string;
}

interface DeliverableTooltipContentProps {
  fetchState: FetchState<Deliverable>;
}

// ============================================================================
// Tooltip Content
// ============================================================================

const DeliverableTooltipContent = memo<DeliverableTooltipContentProps>(
  ({ fetchState }) => {
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="deliverable" />;
    }

    if (fetchState.status === 'idle' || !fetchState.data) {
      return <EmptyState entityType="deliverable" />;
    }

    const deliverable = fetchState.data;
    const { name, status } = deliverable;

    // Deliverable type doesn't strictly have 'assignee' or 'description' in the interface
    // but might come from backend. We'll omit them if not present in type or cast if known to exist.
    // Looking at type definition, it has 'user_id' and 'matches'.
    // For now, let's stick to what's in BaseDeliverable: name, prompt, status.

    const { prompt } = deliverable;

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={DocumentTextIcon} title={name || 'Deliverable'} />

        <TooltipSection withBorder>
          {status && (
            <div className="mb-2">{createStatusBadge({ value: status })}</div>
          )}
          {prompt && (
            <div className="text-xs text-gray-500 line-clamp-3 italic">
              &ldquo;{prompt}&rdquo;
            </div>
          )}
        </TooltipSection>
      </div>
    );
  },
);

DeliverableTooltipContent.displayName = 'DeliverableTooltipContent';

// ============================================================================
// Component
// ============================================================================

export const DeliverableLink: React.FC<DeliverableLinkProps> = ({
  id,
  matterId,
  children,
  className,
  disableNavigation,
  side = 'right',
  align = 'center',
  variant = 'text',
}) => {
  const { data, isLoading, isError, error } =
    deliverablesApi.endpoints.getMatterDeliverable.useQuery(
      { matterId, deliverableId: id },
      { skip: !id || !matterId },
    );

  let fetchState: FetchState<Deliverable>;
  if (isLoading) {
    fetchState = { status: 'pending' };
  } else if (isError) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    fetchState = { status: 'error', error: error! };
  } else if (data) {
    fetchState = { status: 'success', data };
  } else {
    fetchState = { status: 'idle' };
  }

  // Derive label for badge variant
  const badgeLabel = 'DELIVERABLE';

  // Trigger element - styled differently based on whether it's navigable and the variant
  const triggerContent =
    variant === 'badge' ? (
      <span
        className={cn(
          'inline-flex h-5 items-center overflow-hidden rounded border align-middle transition-colors',
          !disableNavigation
            ? 'border-dashed border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
            : 'border-solid border-gray-200 bg-white',
        )}
      >
        <span className="flex h-full items-center border-r border-gray-200 bg-gray-50 px-1.5 text-[9px] font-bold uppercase leading-none tracking-wider text-gray-500">
          {badgeLabel}
        </span>
        <span className="flex h-full items-center px-2 text-xs font-medium leading-none text-gray-900">
          {children}
        </span>
      </span>
    ) : (
      children
    );

  const triggerClassName = cn(
    variant === 'text' && [
      'inline-flex items-center gap-1',
      'text-gray-700',
      'border-b border-dotted border-gray-300',
      'cursor-help',
    ],
    variant === 'badge' && ['cursor-help decoration-0'],
    'transition-colors',
    className,
  );

  const linkClassName = cn(
    variant === 'text' && [
      'inline-flex cursor-pointer items-center gap-1',
      'text-blue-600 hover:text-blue-700',
      'underline decoration-blue-300 decoration-dashed underline-offset-2',
      'hover:decoration-blue-500 hover:decoration-solid',
    ],
    variant === 'badge' && ['cursor-pointer decoration-0'],
    'transition-colors',
    className,
  );

  return (
    <EntityTooltip
      content={<DeliverableTooltipContent fetchState={fetchState} />}
      side={side}
      align={align}
    >
      {disableNavigation ? (
        <span className={triggerClassName}>{triggerContent}</span>
      ) : (
        <Link
          to={`/matters/${matterId}/deliverables/${id}`}
          className={linkClassName}
        >
          {triggerContent}
        </Link>
      )}
    </EntityTooltip>
  );
};
